import { EventSchema, UnifiedSchema, Parameter, SchemaType } from '../types/schema'; // Using new frontend types
import { SchemaDefinition } from '../types/contract'; // Import SchemaDefinition
import env from '../utils/env'; // Import env
import { getSupabaseClient } from '../config/supabase'; // Import the function to get the client
import { v4 as uuidv4 } from 'uuid'; // Import uuid for generating unique IDs

const API_BASE_URL = `${env.apiUrl}/api/schema-definitions`; // Matches backend route, now using env.apiUrl

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const supabase = getSupabaseClient(); // Get the Supabase client instance
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError) {
    console.error('Supabase getSession error:', sessionError);
    // Optionally throw or handle more gracefully
  }

  const token = session?.access_token;
  const headers: Record<string, string> = {}; // Declare headers here
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('No Supabase session token found. API requests might be unauthorized.');
  }
  return headers;
};

// Backend representation of SchemaDefinition, matching the Supabase table structure
interface BackendSchemaDefinition {
  id?: string;
  name: string;
  code_name: string;
  description: string | null;
  parameters: any; // JSONB field that will contain properties and events
  type: string; // 'unified_schema'
  status: 'draft' | 'published' | 'disabled'; // Added 'disabled' status to match frontend type
  inserted_at?: string;
  updated_at?: string;
  integration_id?: string | null; // Add integration_id
}

// Helper to convert frontend UnifiedSchema to backend SchemaDefinition payload
const toBackendSchemaDefinition = (unifiedSchema: UnifiedSchema): BackendSchemaDefinition => {
  // Create a deep copy of properties to avoid modifying the original
  const properties = unifiedSchema.parameters.map(param => {
    // Create a clean copy without UI-specific fields
    const { isExpanded, level, isLastChild, ...cleanParam } = param;
    return cleanParam;
  });

  // Create events array with parameterFullPaths
  const events = unifiedSchema.events.map(event => ({
    id: event.id,
    name: event.name,
    codeName: event.codeName,
    description: event.description,
    parameterFullPaths: event.parameterFullPaths
  }));

  // Create the parameters JSONB structure
  const parametersJson = {
    properties,
    events
  };

  return {
    name: unifiedSchema.name,
    code_name: unifiedSchema.codeName,
    description: unifiedSchema.description,
    parameters: parametersJson, // Store the new structure in the parameters JSONB field
    type: unifiedSchema.type,
    status: unifiedSchema.status,
    integration_id: unifiedSchema.integrationId || null, // Include integration_id
    ...(unifiedSchema.id && { id: unifiedSchema.id }), // Include ID only if it exists (for updates)
  };
};

// Helper to convert backend SchemaDefinition to frontend UnifiedSchema
const toFrontendUnifiedSchema = (backendSchema: BackendSchemaDefinition): UnifiedSchema => {
  // Extract the properties and events from the parameters JSONB
  const parametersJson = backendSchema.parameters;
  
  // Handle both old and new format for backward compatibility
  let properties: Parameter[] = [];
  let events: EventSchema[] = [];
  
  // Check if we have the new format (with properties and events)
  if (parametersJson && typeof parametersJson === 'object' && 'properties' in parametersJson && 'events' in parametersJson) {
    // New format
    properties = (parametersJson.properties || []).map((prop: any) => ({
      ...prop,
      // Ensure UI-specific fields are present
      isExpanded: false,
      parentPaths: prop.parentPaths || [],
    }));
    
    events = (parametersJson.events || []).map((event: any) => ({
      id: event.id,
      name: event.name,
      codeName: event.codeName,
      description: event.description || '',
      parameterFullPaths: event.parameterFullPaths || [],
      isNew: false,
    }));
  } else if (Array.isArray(parametersJson)) {
    // Old format - handle legacy data
    
    const eventsRootParam = parametersJson.find((p: Parameter) => p.codeName === 'events' && p.scope === 'global');

    if (eventsRootParam && eventsRootParam.children) {
      const propertiesMap = new Map<string, Parameter>();

      // Recursive helper to flatten parameters and ensure fullPath
      const flattenParameters = (params: Parameter[], parentFullPath: string, parentPaths: string[]): Parameter[] => {
        const flattened: Parameter[] = [];
        params.forEach(param => {
          const currentFullPath = param.fullPath || `${parentFullPath}.${param.path}`;
          const currentParentPaths = [...parentPaths, parentFullPath];

          // Create a clean parameter object for the properties list
          const cleanParam: Parameter = {
            ...param,
            fullPath: currentFullPath,
            parentPaths: currentParentPaths,
            isExpanded: false, // Ensure UI-specific fields are present
          };

          // Add to map if not already present (deduplication)
          if (!propertiesMap.has(currentFullPath)) {
            propertiesMap.set(currentFullPath, cleanParam);
            flattened.push(cleanParam);
          }

          // Recursively flatten children
          if (param.children && param.children.length > 0) {
            flattened.push(...flattenParameters(param.children, currentFullPath, currentParentPaths));
          }
        });
        return flattened;
      };

      eventsRootParam.children.forEach((eventParam: Parameter) => {
        const event: EventSchema = {
          id: eventParam.id,
          name: eventParam.friendlyName,
          codeName: eventParam.codeName,
          description: eventParam.description || '',
          parameterFullPaths: [],
          isNew: false,
        };

        if (eventParam.children) {
          // Flatten event-specific parameters and add them to the master properties map
          const eventProperties = flattenParameters(eventParam.children, eventParam.fullPath, [eventParam.fullPath]);
          event.parameterFullPaths = eventProperties.map(p => p.fullPath);
        }

        events.push(event);
      });

      // Convert map to array
      properties = Array.from(propertiesMap.values());
    }
  } else {
    console.warn("[toFrontendUnifiedSchema] Unexpected parameters format:", parametersJson);
  }
  
  const frontendSchema: UnifiedSchema = {
    id: backendSchema.id || uuidv4(), // Generate a new UUID if backend ID is missing
    name: backendSchema.name,
    codeName: backendSchema.code_name,
    description: backendSchema.description || '',
    events,
    parameters: properties,
    status: backendSchema.status,
    type: backendSchema.type as SchemaType,
    integrationId: backendSchema.integration_id || undefined, // Map integration_id back to frontend
  };
  return frontendSchema;
};

export const fetchUnifiedSchema = async (id: string, schemaType: SchemaType): Promise<UnifiedSchema | null> => {
  try {
    const authHeaders = await getAuthHeaders();
    const url = `${API_BASE_URL}/${id}?type=${schemaType}`;
    
    const response = await fetch(url, {
      headers: { ...authHeaders },
    });
    
    
    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Error response body:', errorBody);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data: BackendSchemaDefinition = await response.json();
    
    const frontendSchema = toFrontendUnifiedSchema(data);
    
    return frontendSchema;
  } catch (error) {
    console.error(`Error fetching unified schema with ID ${id}:`, error);
    throw error;
  }
};

export const fetchAllUnifiedSchemas = async (schemaType: SchemaType): Promise<UnifiedSchema[]> => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}?type=${schemaType}`, {
      headers: { ...authHeaders },
    });
    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Error response body:', errorBody);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data: BackendSchemaDefinition[] = await response.json();
    return data.map(toFrontendUnifiedSchema);
  } catch (error) {
    console.error(`Error fetching all unified schemas for type ${schemaType}:`, error);
    return [];
  }
};

export const fetchAllSchemaNames = async (): Promise<string[]> => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(API_BASE_URL, {
      headers: { ...authHeaders },
    });
    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Error response body:', errorBody);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data: BackendSchemaDefinition[] = await response.json();
    return data.map(schema => schema.name);
  } catch (error) {
    console.error('Error fetching all schema names:', error);
    return [];
  }
};

// New function to fetch all schema definitions for the Contract Wizard
export const fetchAllSchemaDefinitions = async (): Promise<SchemaDefinition[]> => {
  try {
    const authHeaders = await getAuthHeaders();

    // Fetch Unified Schemas
    const unifiedResponse = await fetch(`${API_BASE_URL}?type=${SchemaType.UnifiedSchema}`, {
      headers: { ...authHeaders },
    });
    if (!unifiedResponse.ok) {
      const errorBody = await unifiedResponse.text();
      console.error('Error response body for unified schemas:', errorBody);
      throw new Error(`HTTP error! status: ${unifiedResponse.status}`);
    }
    const unifiedData: BackendSchemaDefinition[] = await unifiedResponse.json();
    const unifiedSchemas = unifiedData.map(schema => {
      const frontendSchema = toFrontendUnifiedSchema(schema);
      return {
        id: frontendSchema.id,
        name: frontendSchema.name,
        description: frontendSchema.description,
        codeName: frontendSchema.codeName,
        type: frontendSchema.type,
        properties: frontendSchema.parameters.map(p => ({ name: p.friendlyName, path: p.fullPath, type: p.dataTypes[0] || 'string' })),
      };
    });

    // Fetch Customer Schemas
    const customerResponse = await fetch(`${API_BASE_URL}?type=${SchemaType.CustomerSchema}`, {
      headers: { ...authHeaders },
    });
    if (!customerResponse.ok) {
      const errorBody = await customerResponse.text();
      console.error('Error response body for customer schemas:', errorBody);
      throw new Error(`HTTP error! status: ${customerResponse.status}`);
    }
    const customerData: BackendSchemaDefinition[] = await customerResponse.json();
    const customerSchemas = customerData.map(schema => {
      const frontendSchema = toFrontendUnifiedSchema(schema);
      return {
        id: frontendSchema.id,
        name: frontendSchema.name,
        description: frontendSchema.description,
        codeName: frontendSchema.codeName,
        type: frontendSchema.type,
        properties: frontendSchema.parameters.map(p => ({ name: p.friendlyName, path: p.fullPath, type: p.dataTypes[0] || 'string' })),
      };
    });

    // Combine and return
    return [...unifiedSchemas, ...customerSchemas];

  } catch (error) {
    console.error('Error fetching all schema definitions:', error);
    return [];
  }
};

export const createUnifiedSchema = async (unifiedSchema: UnifiedSchema): Promise<UnifiedSchema | null> => {
  try {
    const payload = toBackendSchemaDefinition(unifiedSchema); // Use imported conversion
    const authHeaders = await getAuthHeaders();
    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Error response body:', errorBody);
      throw new Error(`HTTP error! status: ${response.status} - ${errorBody}`);
    }
    const data: BackendSchemaDefinition = await response.json();
    return toFrontendUnifiedSchema(data); // Use imported conversion
  } catch (error) {
    console.error('Error creating unified schema:', error);
    throw error;
  }
};

export const updateUnifiedSchema = async (unifiedSchema: UnifiedSchema): Promise<UnifiedSchema | null> => {
  try {
    if (!unifiedSchema.id) {
      console.error('Cannot update unified schema: ID is missing.');
      throw new Error('Cannot update unified schema: ID is missing.');
    }
    const payload = toBackendSchemaDefinition(unifiedSchema); // Use imported conversion
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/${unifiedSchema.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Error response body:', errorBody);
      throw new Error(`HTTP error! status: ${response.status} - ${errorBody}`);
    }
    const data: BackendSchemaDefinition = await response.json();
    return toFrontendUnifiedSchema(data); // Use imported conversion
  } catch (error) {
    console.error(`Error updating unified schema with ID ${unifiedSchema.id}:`, error);
    throw error;
  }
};

export const deleteUnifiedSchema = async (id: string): Promise<boolean> => {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/${id}`, {
      method: 'DELETE',
      headers: {
        ...authHeaders,
      },
    });
    if (!response.ok) {
      const errorBody = await response.text();
      console.error('Error response body:', errorBody);
      throw new Error(`HTTP error! status: ${response.status} - ${errorBody}`);
    }
    return true;
  } catch (error) {
    console.error(`Error deleting unified schema with ID ${id}:`, error);
    throw error;
  }
};
